import React, { useState, useEffect } from 'react';
import { Heart, MapPin, Star, AlertCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import userService from '../../services/userService';

const VendorCard = ({ vendor, showSaveButton = true, compact = false, onToggleComparison, isSelectedForComparison, canAddToComparison }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get vendor's featured image
  const getFeaturedImage = () => {
    const featuredImage = vendor?.vendor_gallery?.find(img => img?.is_featured);
    return featuredImage?.image_url || vendor?.vendor_gallery?.[0]?.image_url || '/assets/images/no_image.png';
  };

  // Calculate average rating
  const getAverageRating = () => {
    if (!vendor?.reviews || vendor.reviews.length === 0) return 0;
    const total = vendor.reviews.reduce((sum, review) => sum + (review?.rating || 0), 0);
    return Math.round((total / vendor.reviews.length) * 10) / 10;
  };

  // Format price display
  const formatPrice = (price) => {
    if (!price) return 'Contact for pricing';
    return `Starting at $${price.toLocaleString()}`;
  };

  // Check if vendor is saved on component mount
  useEffect(() => {
    const checkSavedStatus = async () => {
      if (user?.id && vendor?.id) {
        try {
          const result = await userService.isVendorSaved(user.id, vendor.id);
          if (result?.success) {
            setIsSaved(result.data);
          }
        } catch (error) {
          console.log('Error checking saved status:', error);
        }
      } else {
        setIsSaved(false);
      }
    };

    checkSavedStatus();
  }, [user?.id, vendor?.id]);

  // Handle save/unsave vendor
  const handleSaveVendor = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    if (!vendor?.id) {
      setError('Invalid vendor data');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      if (isSaved) {
        const result = await userService.removeSavedVendor(user.id, vendor.id);
        if (result?.success) {
          setIsSaved(false);
        } else {
          setError(result?.error || 'Failed to remove from saved vendors');
        }
      } else {
        const result = await userService.saveVendor(user.id, vendor.id);
        if (result?.success) {
          setIsSaved(true);
        } else {
          setError(result?.error || 'Failed to save vendor');
        }
      }
    } catch (error) {
      console.log('Error saving vendor:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle contact vendor
  const handleContactVendor = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login');
      return;
    }

    // Navigate to vendor profile with contact intent
    navigate(`/vendor-profile/${vendor?.id}?contact=true`);
  };

  // Handle comparison toggle
  const handleComparisonToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!vendor?.id) {
      setError('Invalid vendor data');
      return;
    }

    if (onToggleComparison) {
      onToggleComparison(vendor);
    }
  };

  // Generate vendor slug from business name
  const generateSlug = (name) => {
    return name?.toLowerCase()?.replace(/[^a-z0-9]+/g, '-')?.replace(/(^-|-$)+/g, '') || '';
  };

  const averageRating = getAverageRating();
  const reviewCount = vendor?.reviews?.length || 0;
  const vendorSlug = generateSlug(vendor?.business_name);

  if (compact) {
    return (
      <Link 
        to={`/vendor-profile/${vendor?.id}`}
        className="block bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
      >
        <div className="flex p-4">
          <div className="flex-shrink-0">
            <img
              src={getFeaturedImage()}
              alt={vendor?.business_name || 'Vendor'}
              className="h-16 w-16 rounded-lg object-cover"
              onError={(e) => {
                e.target.src = '/assets/images/no_image.png';
              }}
            />
          </div>
          
          <div className="ml-4 flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {vendor?.business_name}
                </h3>
                <p className="text-xs text-gray-500 capitalize">
                  {vendor?.category?.replace('_', ' ')}
                </p>
                
                {vendor?.city && vendor?.state && (
                  <div className="flex items-center mt-1">
                    <MapPin className="h-3 w-3 text-gray-400 mr-1" />
                    <span className="text-xs text-gray-500">
                      {vendor.city}, {vendor.state}
                    </span>
                  </div>
                )}
              </div>
              
              {averageRating > 0 && (
                <div className="flex items-center ml-2">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-gray-600 ml-1">
                    {averageRating}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={getFeaturedImage()}
          alt={vendor?.business_name || 'Vendor'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            e.target.src = '/assets/images/no_image.png';
          }}
        />
        
        {/* Save Button */}
        {showSaveButton && (
          <button
            onClick={handleSaveVendor}
            disabled={isLoading}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 ${
              isSaved 
                ? 'bg-red-500 text-white shadow-lg' 
                : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isSaved ? 'Remove from saved' : 'Save vendor'}
          >
            <Heart 
              className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} 
            />
          </button>
        )}

        {/* Comparison Button */}
        {onToggleComparison && (
          <button
            onClick={handleComparisonToggle}
            disabled={!canAddToComparison && !isSelectedForComparison}
            className={`absolute top-3 right-12 p-2 rounded-full transition-all duration-200 ${
              isSelectedForComparison 
                ? 'bg-blue-500 text-white shadow-lg' 
                : 'bg-white/80 text-gray-600 hover:bg-white hover:text-blue-500'
            } ${(!canAddToComparison && !isSelectedForComparison) ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={isSelectedForComparison ? 'Remove from comparison' : 'Add to comparison'}
          >
            <div className="text-xs font-bold">
              {isSelectedForComparison ? '✓' : '+'}
            </div>
          </button>
        )}

        {/* Verified Badge */}
        {vendor?.is_verified && (
          <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
            Verified
          </div>
        )}

        {/* Featured Badge */}
        {vendor?.is_featured && (
          <div className="absolute bottom-3 left-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
            Featured
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4">
        {/* Error Message */}
        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-xs text-red-600">{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-3">
          <Link 
            to={`/vendor-profile/${vendor?.id}`}
            className="block group-hover:text-indigo-600 transition-colors duration-200"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
              {vendor?.business_name}
            </h3>
          </Link>
          
          <p className="text-sm text-gray-500 capitalize mb-2">
            {vendor?.category?.replace('_', ' ')}
          </p>

          {/* Rating and Reviews */}
          {averageRating > 0 && (
            <div className="flex items-center mb-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(averageRating)
                        ? 'text-yellow-400 fill-current' :'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600 ml-2">
                {averageRating} ({reviewCount} reviews)
              </span>
            </div>
          )}

          {/* Location */}
          {vendor?.city && vendor?.state && (
            <div className="flex items-center mb-2">
              <MapPin className="h-4 w-4 text-gray-400 mr-1" />
              <span className="text-sm text-gray-600">
                {vendor.city}, {vendor.state}
              </span>
            </div>
          )}

          {/* Price */}
          <div className="text-lg font-semibold text-indigo-600 mb-3">
            {formatPrice(vendor?.starting_price)}
          </div>
        </div>

        {/* Description */}
        {vendor?.description && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {vendor.description}
          </p>
        )}

        {/* Services/Tags */}
        {vendor?.services && vendor.services.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {vendor.services.slice(0, 3).map((service, index) => (
                <span
                  key={index}
                  className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                >
                  {service}
                </span>
              ))}
              {vendor.services.length > 3 && (
                <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                  +{vendor.services.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Link
            to={`/vendor-profile/${vendor?.id}`}
            className="flex-1 bg-indigo-600 text-white text-center py-2 px-4 rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium text-sm"
          >
            View Profile
          </Link>
          
          <button
            onClick={handleContactVendor}
            className="flex-1 bg-gray-100 text-gray-700 text-center py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium text-sm"
          >
            Contact
          </button>
        </div>

        {/* Response Time */}
        {vendor?.response_time && (
          <div className="mt-3 text-center">
            <span className="text-xs text-gray-500">
              Typically responds in {vendor.response_time}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorCard;